import api from './api';

// Helper: handle a fetch-based api response that returns either:
//   { success: true, data: {...} }  on success
//   { success: false, message: '...' } on error
const unwrap = (res, fallback) => {
  if (res && res.success === true) {
    return { success: true, data: res.data };
  }
  return {
    success: false,
    error: (res && (res.message || res.error)) || fallback
  };
};

class SurveyService {
  async listSurveys(params = {}) {
    try {
      // Strip undefined/null/empty values so URLSearchParams doesn't serialize
      // them as the literal string "undefined" (which the backend would then
      // try to match against survey.status, returning zero results).
      const cleaned = Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
      );
      const res = await api.get('/admin/surveys', cleaned);
      return unwrap(res, 'Failed to list surveys');
    } catch (e) {
      return { success: false, error: e.message || 'Failed to list surveys' };
    }
  }

  async getSurvey(id) {
    try {
      const res = await api.get(`/admin/surveys/${id}`);
      return unwrap(res, 'Failed to fetch survey');
    } catch (e) {
      return { success: false, error: e.message || 'Failed to fetch survey' };
    }
  }

  async createSurvey(payload) {
    try {
      const res = await api.post('/admin/surveys', payload);
      return unwrap(res, 'Failed to create survey');
    } catch (e) {
      return { success: false, error: e.message || 'Failed to create survey' };
    }
  }

  async updateSurvey(id, payload) {
    try {
      const res = await api.put(`/admin/surveys/${id}`, payload);
      return unwrap(res, 'Failed to update survey');
    } catch (e) {
      return { success: false, error: e.message || 'Failed to update survey' };
    }
  }

  async deleteSurvey(id) {
    try {
      const res = await api.delete(`/admin/surveys/${id}`);
      return unwrap(res, 'Failed to delete survey');
    } catch (e) {
      return { success: false, error: e.message || 'Failed to delete survey' };
    }
  }

  async duplicateSurvey(id) {
    try {
      const res = await api.post(`/admin/surveys/${id}/duplicate`);
      return unwrap(res, 'Failed to duplicate survey');
    } catch (e) {
      return { success: false, error: e.message || 'Failed to duplicate survey' };
    }
  }

  async getResponses(id, params = {}) {
    try {
      const res = await api.get(`/admin/surveys/${id}/responses`, params);
      return unwrap(res, 'Failed to fetch responses');
    } catch (e) {
      return { success: false, error: e.message || 'Failed to fetch responses' };
    }
  }

  async getResponseDetail(surveyId, responseId) {
    try {
      const res = await api.get(`/admin/surveys/${surveyId}/responses/${responseId}`);
      return unwrap(res, 'Failed to fetch response');
    } catch (e) {
      return { success: false, error: e.message || 'Failed to fetch response' };
    }
  }

  async getAnalytics(id) {
    try {
      const res = await api.get(`/admin/surveys/${id}/analytics`);
      return unwrap(res, 'Failed to fetch analytics');
    } catch (e) {
      return { success: false, error: e.message || 'Failed to fetch analytics' };
    }
  }

  async exportResponses(id) {
    return api.getBlob(`/admin/surveys/${id}/responses/export`);
  }

  // Public endpoints (no auth required)
  async getPublicSurvey(slug) {
    try {
      const res = await api.get(`/public-surveys/${slug}`);
      return unwrap(res, 'Failed to load survey');
    } catch (e) {
      return { success: false, error: e.message || 'Failed to load survey' };
    }
  }

  // Admin preview (auth required) — bypasses status/schedule checks
  async getSurveyPreview(id) {
    try {
      const res = await api.get(`/admin/surveys/${id}/preview`);
      return unwrap(res, 'Failed to load survey preview');
    } catch (e) {
      return { success: false, error: e.message || 'Failed to load survey preview' };
    }
  }

  async submitResponse(slug, payload) {
    try {
      const res = await api.post(`/public-surveys/${slug}/responses`, payload);
      return unwrap(res, 'Failed to submit response');
    } catch (e) {
      return { success: false, error: e.message || 'Failed to submit response' };
    }
  }
}

export default new SurveyService();
